import { Component, OnInit, OnDestroy } from '@angular/core';
import ProductOrdersModel from 'src/app/core/models/orders.model';
import { ProductOrdersService } from 'src/app/core/services/order.service';
import * as moment from 'moment';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';

/**
 * OrdersComponent
 *
 * This component is responsible for managing Product Orders within the Admin Panel.
 * It provides a user interface for Business Admins and Provider Managers to view, filter,
 * and manage orders (approve/decline).
 *
 * Key Responsibilities:
 * - Fetch and display a list of product orders based on user role.
 * - Provide filtering functionality by date range (Today, Last Week, Last Month, Last Year).
 * - Provide filtering functionality by order status (New, Approved, Declined).
 * - Allow authorized users to approve or decline pending orders.
 * - Implement search functionality to filter orders by person name or business name.
 * - Manage UI state for loading indicators, popups, and messages.
 *
 * User Roles:
 * - Business Admin: Can view and manage orders for their provider business.
 * - Provider Manager: Can view and manage orders assigned to them.
 *
 * Features:
 * - **Order Management**: Approve or Decline orders with remarks.
 * - **Date Filtering**: Filter orders by specific timeframes.
 * - **Status Filtering**: View orders based on their current status.
 * - **Search**: Real-time search by person or business name.
 * - **Role-based Data Loading**: Loads orders based on the logged-in user's role and ID.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Retrieves user details from local storage.
 * 3. Fetches orders from `ProductOrdersService` based on role.
 * 4. Applies default date filter (All) and status filter (Booked/New).
 * 5. User interactions (filter, search, approve/decline) trigger state updates and service calls.
 * 6. `acceptDeclineOrders` updates order status in backend and refreshes the list.
 *
 * @component
 * @selector app-orders
 * @example
 * <app-orders></app-orders>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss']
})
export class OrdersComponent implements OnInit, OnDestroy {
  /**
   * Flag indicating if the page data has finished loading.
   * @type {boolean}
   */
  public pgLoad: boolean;

  /**
   * Current search query string.
   * @type {string}
   */
  public search: string;

  /**
   * Selected date filter range (e.g., 'today', 'lastweek', 'All').
   * @type {string}
   */
  public noOfDays: string;

  /**
   * Current date string (YYYY-MM-DD).
   * @type {string}
   */
  public todayDate: string;

  /**
   * Display text for the current status filter (e.g., 'New Orders').
   * @type {string}
   */
  public statusText: string;

  /**
   * Index of the currently active filter tab.
   * @type {number}
   */
  public activeIndex: number;

  /**
   * Current order status filter value (e.g., 'booked', 'approved', 'declined').
   * @type {string}
   */
  public orderStatus: string;

  /**
   * Comment/remarks entered when approving or declining an order.
   * @type {string}
   */
  public orderComment: string;

  /**
   * Date string for one week ago.
   * @type {string}
   */
  public weekfromDate: string;

  /**
   * Date string for one year ago.
   * @type {string}
   */
  public yearfromDate: string;

  /**
   * ID of the logged-in person.
   * @type {string}
   */
  public loginPersonId: string;

  /**
   * Date string for one month ago.
   * @type {string}
   */
  public monthfromDate: string;

  /**
   * Flag indicating if no data is available to display.
   * @type {boolean}
   */
  public NoDataMessage: boolean;

  /**
   * Role of the logged-in user.
   * @type {string}
   */
  public loginPersonType: string;

  /**
   * Business category of the logged-in user.
   * @type {string}
   */
  public loginBusinessCategory: string;

  /**
   * Active filter index (redundant with activeIndex, used for UI state).
   * @type {number}
   */
  public activeFilter: number;

  /**
   * Flag to display the Approve button in the status change popup.
   * @type {boolean}
   */
  public approveBtnDisplay: boolean;

  /**
   * Flag to display the Decline button in the status change popup.
   * @type {boolean}
   */
  public declineBtnDisplay: boolean;

  /**
   * Flag to display the comments block in the status change popup.
   * @type {boolean}
   */
  public commentsBlockDisplay: boolean;

  /**
   * Flag to display the filters popup.
   * @type {Boolean}
   */
  public filtersPopupDisplay: Boolean;

  /**
   * Business ID of the logged-in user.
   * @type {string}
   */
  public loginBusinessId: string;

  /**
   * Array of all fetched product orders.
   * @type {Array<ProductOrdersModel>}
   */
  public productOrders: Array<ProductOrdersModel>;

  /**
   * Model for a single product order (not actively used in list view).
   * @type {ProductOrdersModel}
   */
  public productOrder: ProductOrdersModel;

  /**
   * Array of product orders currently displayed (filtered by status).
   * @type {any[]}
   */
  public productOrdersFinalData = [];

  /**
   * Array of product orders filtered by date.
   * @type {any[]}
   */
  public productOrdersFilterData = [];

  /**
   * Flag to trigger clearing of the search input.
   * @type {boolean}
   */
  public clearSearchInput: boolean;

  /**
   * Constructor for OrdersComponent
   *
   * Initializes the component and injects required services.
   *
   * @constructor
   * @param {ProductOrdersService} productOrdersService - Service for Product Orders API operations.
   * @service ProductOrdersService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private productOrdersService: ProductOrdersService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) { }

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Tracks the current route.
   * 2. Resets component state flags.
   * 3. Retrieves user details from local storage.
   * 4. Initializes date ranges.
   * 5. Fetches product orders based on user role (Business Admin vs Provider Manager).
   * 6. Applies default filters (All days, New Orders).
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.pgLoad = false;
    this.activeFilter = 0;
    this.clearSearchInput = false;
    this.commentsBlockDisplay = false;
    this.filtersPopupDisplay = false;
    this.loginPersonId = localStorage.getItem('loginPersonId');
    this.loginPersonType = localStorage.getItem('loginPersonType');
    this.loginBusinessId = localStorage.getItem('loginBusinessID');
    this.loginBusinessCategory = localStorage.getItem('loginBusinessCategory');
    this.initDates();
    try {
      if (this.loginPersonType == 'Business Admin') {
        this.productOrders = await this.productOrdersService.getProductOrdersByProviderBusinessID(this.loginBusinessId);
      } else {
        this.productOrders = await this.productOrdersService.getProductOrdersByProviderManagerID(this.loginPersonId);
      }
      if (this.productOrders) {
        this.pgLoad = true;
        this.orderStatus = 'booked';
        this.noOfDays = 'All';
        this.statusText = 'New Orders';
        this.getDataOnDateBasis(this.noOfDays);
        this.NoDataMessage = false;
        this.approveBtnDisplay = false;
        this.declineBtnDisplay = false;
        this.search = '';
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.productOrdersService.cancelOnGoingHttpRequests();
  }

  /**
   * Initializes date variables for filtering.
   * Calculates dates for today, last week, last month, and last year.
   *
   * @method initDates
   * @returns {void}
   */
  public initDates() {
    let date = new Date();
    date.setDate(date.getDate() - 7);
    this.weekfromDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
    date = new Date();
    date.setDate(date.getDate() - 30);
    this.monthfromDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
    date = new Date();
    date.setDate(date.getDate() - 365);
    this.yearfromDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
    date = new Date();
    this.todayDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
  }

  /**
   * Opens the status filter popup.
   *
   * @method openFilterPopUp
   * @returns {void}
   */
  public openFilterPopUp() {
    this.filtersPopupDisplay = true;
  }

  /**
   * Closes the status filter popup.
   *
   * @method closeFilterPopup
   * @returns {void}
   */
  public closeFilterPopup() {
    this.filtersPopupDisplay = false;
  }

  /**
   * Filters product orders based on the selected date range.
   *
   * - Filters: 'today', 'lastweek', 'lastmonth', 'lastyear', 'All'.
   * - Updates `productOrdersFilterData`.
   * - Calls `ordersFinalData` to apply status filter on the date-filtered data.
   *
   * @method getDataOnDateBasis
   * @param {string} type - The date range type.
   * @returns {void}
   */
  public getDataOnDateBasis(type: string) {
    this.filtersPopupDisplay = false;
    this.productOrdersFilterData = [];
    let toDate = (moment(new Date()).format('YYYY-MM-DD')).toString();
    if (type == 'today') {
      let startDate = new Date(this.todayDate);
      let endDate = new Date(toDate);
      this.productOrdersFilterData = this.productOrders.filter(a => { let date = new Date(a.date_ordered); return (date >= startDate && date <= endDate); });
    } else if (type == 'lastweek') {
      let startDate = new Date(this.weekfromDate);
      let endDate = new Date(toDate);
      this.productOrdersFilterData = this.productOrders.filter(a => { let date = new Date(a.date_ordered); return (date >= startDate && date <= endDate); });
    } else if (type == 'lastmonth') {
      let startDate = new Date(this.monthfromDate);
      let endDate = new Date(toDate);
      this.productOrdersFilterData = this.productOrders.filter(a => { let date = new Date(a.date_ordered); return (date >= startDate && date <= endDate); });
    } else if (type == 'lastyear') {
      let startDate = new Date(this.yearfromDate);
      let endDate = new Date(toDate);
      this.productOrdersFilterData = this.productOrders.filter(a => { let date = new Date(a.date_ordered); return (date >= startDate && date <= endDate); });
    } else {
      this.productOrdersFilterData = this.productOrders;
    }
    if (this.productOrdersFilterData && this.productOrdersFilterData.length > 0) {
      this.ordersFinalData(this.orderStatus);
    } else {
      this.NoDataMessage = true;
      this.productOrdersFinalData = [];
    }
  }

  /**
   * Filters product orders based on status and updates the UI.
   *
   * - Statuses: 'booked' (New), 'approved', 'declined'.
   * - Updates `activeFilter`, `orderStatus`, `statusText`.
   *
   * @method getDataOnStatusBasis
   * @param {number} tabNumber - The index of the selected tab.
   * @param {string} status - The order status to filter by.
   * @returns {void}
   */
  public getDataOnStatusBasis(tabNumber: number, status: string) {
    this.search = '';
    this.activeFilter = tabNumber;
    this.orderStatus = status;
    this.filtersPopupDisplay = false;
    if (this.orderStatus == 'approved') {
      this.statusText = 'Approved Orders';
    } else if (this.orderStatus == 'declined') {
      this.statusText = 'Declined Orders';
    } else {
      this.statusText = 'New Orders'
    }
    this.ordersFinalData(status);
  }

  /**
   * Finalizes the list of displayed orders by filtering `productOrdersFilterData` by status.
   * Updates `productOrdersFinalData` and `NoDataMessage`.
   *
   * @method ordersFinalData
   * @param {string} status - The order status to filter by.
   * @returns {void}
   */
  public ordersFinalData(status: string) {
    this.productOrdersFinalData = [];
    let productOrdersFinalData = this.productOrdersFilterData.filter(order => order && order.status == status);
    if (productOrdersFinalData && productOrdersFinalData.length > 0) {
      this.productOrdersFinalData = productOrdersFinalData;
    } else {
      this.NoDataMessage = true;
    }
  }

  /**
   * Prepares the UI for changing the status of a pending order (Approve/Decline).
   * Opens the comments block and sets the target status.
   *
   * @method changeStatus
   * @param {string} status - The target status ('approved' or 'declined').
   * @param {number} index - The index of the order in the list.
   * @returns {void}
   */
  public changeStatus(status: string, index: number) {
    if (status == 'approved') {
      this.approveBtnDisplay = true;
      this.declineBtnDisplay = false;
    } else {
      this.approveBtnDisplay = false;
      this.declineBtnDisplay = true;
    }
    this.orderComment = undefined;
    this.commentsBlockDisplay = true;
    this.activeFilter = index;
    this.activeIndex = index;
    this.orderStatus = status;
  }

  /**
   * Submits the status change (Approve/Decline) for an order.
   *
   * - Updates order status, remarks, and updated_by fields.
   * - Calls `updateOneByID` service method.
   * - Refreshes the list on success.
   *
   * @async
   * @method acceptDeclineOrders
   * @param {ProductOrdersModel} productOrder - The order object to update.
   * @param {number} productIndex - The index of the order in the list.
   * @returns {Promise<void>}
   */
  public async acceptDeclineOrders(productOrder: ProductOrdersModel, productIndex: number) {
    productOrder.status = this.orderStatus;
    productOrder.order_remarks = this.orderComment;
    productOrder.updated_by = this.loginPersonId;
    try {
      let productOrderUpdated = await this.productOrdersService.updateOneByID(productOrder.id, productOrder);
      if (productOrderUpdated) {
        this.orderComment = undefined;
        this.productOrdersFinalData.splice(productIndex, 1);
      }
      this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Filters the displayed orders based on search input.
   *
   * - Searches by `generated_by_person_name` or `ordered_to_business_name`.
   * - Case-insensitive and ignores special characters.
   *
   * @async
   * @method searchOrderItems
   * @param {KeyboardEvent} event - The keyboard event.
   * @returns {Promise<void>}
   */
  public async searchOrderItems(event: KeyboardEvent) {
    const value = this.search && this.search.replace(/[-_]|\s/g, "").toLowerCase();
    if (value && (event.key != 'Backspace' || event.key == 'Backspace')) {
      this.NoDataMessage = false;
      this.clearSearchInput = true;
      this.getDataOnDateBasis(this.noOfDays);
      this.productOrdersFinalData = this.productOrdersFinalData.filter(item => item && item.generated_by_person_name.replace(/[-_]|\s/g, "").toLowerCase().startsWith(value) ||
        item && item.ordered_to_business_name.replace(/[-_]|\s/g, "").toLowerCase().startsWith(value));
      this.NoDataMessage = this.productOrdersFinalData && this.productOrdersFinalData.length <= 0 ? true : false;
    } else if (!value) {
      this.getDataOnDateBasis(this.noOfDays);
      this.NoDataMessage = false;
      this.clearSearchInput = false;
    }
  }
}
